/**
 * Node Parameter Validators
 *
 * Validates node-specific parameters based on node type schemas.
 */

import {
  WorkflowNodeJSON,
  WorkflowJSONValidationError,
  WorkflowJSONValidationWarning,
} from '../types';
import {
  NODE_PARAMETER_SCHEMAS,
  ParameterDefinition,
  getNodeParameterSchema,
  isKnownNodeType,
} from '../types/node-parameter-schemas';

/**
 * Result of node parameter validation
 */
export interface NodeParameterValidationResult {
  errors: WorkflowJSONValidationError[];
  warnings: WorkflowJSONValidationWarning[];
}

/**
 * Validate all nodes' parameters
 */
export function validateAllNodeParameters(
  nodes: WorkflowNodeJSON[]
): NodeParameterValidationResult {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  nodes.forEach((node, index) => {
    const result = validateNodeParameters(node, index);
    errors.push(...result.errors);
    warnings.push(...result.warnings);
  });

  return { errors, warnings };
}

/**
 * Validate a single node's parameters
 */
export function validateNodeParameters(
  node: WorkflowNodeJSON,
  index: number
): NodeParameterValidationResult {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  const nodeType = node.type || node.data?.nodeType;

  if (!nodeType) {
    // Already handled by structure validator
    return { errors, warnings };
  }

  // Check if node type is known
  if (!isKnownNodeType(nodeType)) {
    warnings.push({
      code: 'UNKNOWN_NODE_TYPE',
      message: `Unknown node type "${nodeType}" - skipping parameter validation`,
      path: `nodes[${index}].type`,
      nodeId: node.id,
      suggestion: 'Verify the node type is correct or use a supported node type',
    });
    return { errors, warnings };
  }

  const schema = getNodeParameterSchema(nodeType);
  if (!schema) {
    return { errors, warnings };
  }

  // Get parameters from node data
  const parameters = node.data?.parameters || {};

  // Validate required parameters
  schema.parameters.forEach((paramDef) => {
    if (paramDef.required) {
      const result = validateRequiredParameter(
        node,
        paramDef,
        parameters,
        index
      );
      errors.push(...result.errors);
    }
  });

  // Validate parameter types
  schema.parameters.forEach((paramDef) => {
    const value = parameters[paramDef.name];
    if (value !== undefined && value !== null) {
      const result = validateParameterType(node, paramDef, value, index);
      errors.push(...result.errors);
      warnings.push(...result.warnings);
    }
  });

  return { errors, warnings };
}

/**
 * Validate a required parameter is present
 */
function validateRequiredParameter(
  node: WorkflowNodeJSON,
  paramDef: ParameterDefinition,
  parameters: Record<string, unknown>,
  index: number
): { errors: WorkflowJSONValidationError[] } {
  const errors: WorkflowJSONValidationError[] = [];
  const value = parameters[paramDef.name];

  // Check for value presence
  if (value === undefined || value === null) {
    // Check if parameter might be in node.data directly (legacy format)
    const directValue = (node.data as Record<string, unknown>)?.[paramDef.name];

    if (
      (directValue === undefined || directValue === null) &&
      !hasDynamicFieldValue(parameters, paramDef.dynamicFieldEntityNames)
    ) {
      errors.push({
        code: 'MISSING_NODE_PARAMETER',
        message: `${getNodeDisplayName(node)} requires "${paramDef.name}" parameter`,
        path: `nodes[${index}].data.parameters.${paramDef.name}`,
        nodeId: node.id,
        suggestion: paramDef.description
          ? `Add "${paramDef.name}" parameter: ${paramDef.description}`
          : `Add the required "${paramDef.name}" parameter`,
      });
    }
  } else if (typeof value === 'string' && value.trim() === '') {
    errors.push({
      code: 'MISSING_NODE_PARAMETER',
      message: `${getNodeDisplayName(node)} has empty "${paramDef.name}" parameter`,
      path: `nodes[${index}].data.parameters.${paramDef.name}`,
      nodeId: node.id,
      suggestion: `Provide a value for the "${paramDef.name}" parameter`,
    });
  } else if (Array.isArray(value) && value.length === 0 && paramDef.type === 'array') {
    errors.push({
      code: 'MISSING_NODE_PARAMETER',
      message: `${getNodeDisplayName(node)} has empty "${paramDef.name}" array`,
      path: `nodes[${index}].data.parameters.${paramDef.name}`,
      nodeId: node.id,
      suggestion: `Add at least one item to the "${paramDef.name}" array`,
    });
  }

  return { errors };
}

/**
 * Check whether the node's `dynamicFields` mapping array supplies a value
 * for one of the given entity names (e.g. a "Domain" field mapped from an
 * upstream node's output, in place of a flat parameter value).
 */
function hasDynamicFieldValue(
  parameters: Record<string, unknown>,
  entityNames?: string[]
): boolean {
  if (!entityNames || entityNames.length === 0) {
    return false;
  }

  const dynamicFields = parameters.dynamicFields;
  if (!Array.isArray(dynamicFields)) {
    return false;
  }

  const normalizedNames = entityNames.map((name) => name.toLowerCase());
  return dynamicFields.some((field) => {
    const entityName = (field as Record<string, unknown>)?.entityName;
    const value = (field as Record<string, unknown>)?.value;
    return (
      typeof entityName === 'string' &&
      normalizedNames.includes(entityName.toLowerCase()) &&
      typeof value === 'string' &&
      value.trim() !== ''
    );
  });
}

/**
 * Validate parameter type matches expected type
 */
function validateParameterType(
  node: WorkflowNodeJSON,
  paramDef: ParameterDefinition,
  value: unknown,
  index: number
): { errors: WorkflowJSONValidationError[]; warnings: WorkflowJSONValidationWarning[] } {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  switch (paramDef.type) {
    case 'string':
      if (typeof value !== 'string') {
        errors.push({
          code: 'INVALID_NODE_PARAMETER',
          message: `${getNodeDisplayName(node)} parameter "${paramDef.name}" must be a string`,
          path: `nodes[${index}].data.parameters.${paramDef.name}`,
          nodeId: node.id,
          suggestion: `Convert the value to a string`,
        });
      }
      break;

    case 'number':
      if (typeof value !== 'number') {
        errors.push({
          code: 'INVALID_NODE_PARAMETER',
          message: `${getNodeDisplayName(node)} parameter "${paramDef.name}" must be a number`,
          path: `nodes[${index}].data.parameters.${paramDef.name}`,
          nodeId: node.id,
          suggestion: `Provide a numeric value`,
        });
      }
      break;

    case 'boolean':
      if (typeof value !== 'boolean') {
        errors.push({
          code: 'INVALID_NODE_PARAMETER',
          message: `${getNodeDisplayName(node)} parameter "${paramDef.name}" must be a boolean`,
          path: `nodes[${index}].data.parameters.${paramDef.name}`,
          nodeId: node.id,
          suggestion: `Provide true or false`,
        });
      }
      break;

    case 'array':
      if (!Array.isArray(value)) {
        errors.push({
          code: 'INVALID_NODE_PARAMETER',
          message: `${getNodeDisplayName(node)} parameter "${paramDef.name}" must be an array`,
          path: `nodes[${index}].data.parameters.${paramDef.name}`,
          nodeId: node.id,
          suggestion: `Provide an array value`,
        });
      } else if (paramDef.itemSchema && value.length > 0) {
        // Validate array items
        value.forEach((item, itemIndex) => {
          if (typeof item === 'object' && item !== null) {
            paramDef.itemSchema!.forEach((itemParamDef) => {
              if (itemParamDef.required) {
                const itemValue = (item as Record<string, unknown>)[itemParamDef.name];
                if (itemValue === undefined || itemValue === null) {
                  errors.push({
                    code: 'MISSING_NODE_PARAMETER',
                    message: `${getNodeDisplayName(node)} ${paramDef.name}[${itemIndex}] is missing "${itemParamDef.name}"`,
                    path: `nodes[${index}].data.parameters.${paramDef.name}[${itemIndex}].${itemParamDef.name}`,
                    nodeId: node.id,
                    suggestion: itemParamDef.description || `Add "${itemParamDef.name}" to the item`,
                  });
                }
              }
            });
          }
        });
      }
      break;

    case 'object':
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        errors.push({
          code: 'INVALID_NODE_PARAMETER',
          message: `${getNodeDisplayName(node)} parameter "${paramDef.name}" must be an object`,
          path: `nodes[${index}].data.parameters.${paramDef.name}`,
          nodeId: node.id,
          suggestion: `Provide an object value`,
        });
      }
      break;

    case 'enum':
      if (paramDef.enumValues && !paramDef.enumValues.includes(value as string)) {
        errors.push({
          code: 'INVALID_NODE_PARAMETER',
          message: `${getNodeDisplayName(node)} parameter "${paramDef.name}" has invalid value "${value}"`,
          path: `nodes[${index}].data.parameters.${paramDef.name}`,
          nodeId: node.id,
          suggestion: `Use one of: ${paramDef.enumValues.join(', ')}`,
        });
      }
      break;
  }

  return { errors, warnings };
}

/**
 * Validate conditional node branches (IF nodes must have true/false branches)
 */
export function validateConditionalBranches(
  nodes: WorkflowNodeJSON[],
  edges: { source: string; target: string; sourceHandle?: string }[]
): { errors: WorkflowJSONValidationError[]; warnings: WorkflowJSONValidationWarning[] } {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  const conditionalNodeTypes = new Set(['if', 'conditionNode', 'condition']);

  nodes.forEach((node, index) => {
    if (conditionalNodeTypes.has(node.type)) {
      // Find edges from this node
      const outgoingEdges = edges.filter((e) => e.source === node.id);
      const hasTrue = outgoingEdges.some((e) => e.sourceHandle === 'true');
      const hasFalse = outgoingEdges.some((e) => e.sourceHandle === 'false');

      if (!hasTrue && !hasFalse) {
        warnings.push({
          code: 'MISSING_CONDITIONAL_BRANCHES',
          message: `Conditional node "${node.data?.label || node.id}" has no branch connections`,
          path: `nodes[${index}]`,
          nodeId: node.id,
          suggestion: 'Add edges with sourceHandle "true" and "false" for both branches',
        });
      } else if (!hasTrue) {
        warnings.push({
          code: 'MISSING_TRUE_BRANCH',
          message: `Conditional node "${node.data?.label || node.id}" is missing TRUE branch`,
          path: `nodes[${index}]`,
          nodeId: node.id,
          suggestion: 'Add an edge with sourceHandle "true" for the TRUE branch',
        });
      } else if (!hasFalse) {
        warnings.push({
          code: 'MISSING_FALSE_BRANCH',
          message: `Conditional node "${node.data?.label || node.id}" is missing FALSE branch`,
          path: `nodes[${index}]`,
          nodeId: node.id,
          suggestion: 'Add an edge with sourceHandle "false" for the FALSE branch',
        });
      }
    }
  });

  return { errors, warnings };
}

/**
 * Get display name for a node (for error messages)
 */
function getNodeDisplayName(node: WorkflowNodeJSON): string {
  if (node.data?.label) {
    return `Node "${node.data.label}"`;
  }
  if (node.data?.name) {
    return `Node "${node.data.name}"`;
  }
  const nodeType = node.type || node.data?.nodeType;
  if (nodeType) {
    return `${nodeType} node "${node.id}"`;
  }
  return `Node "${node.id}"`;
}

/**
 * Get node type display name (for suggestions)
 */
export function getNodeTypeDisplayName(nodeType: string): string {
  const schema = NODE_PARAMETER_SCHEMAS[nodeType];
  return schema?.description || nodeType;
}
